/**
 * api/git.js — queries e mutations para integração Git.
 *
 * Endpoints (Onda 3 da API real):
 *   GET    /projects/{id}/git-repositories                          — lista repos do projeto
 *   POST   /projects/{id}/git-repositories                          — adicionar repo
 *   DELETE /projects/{id}/git-repositories/{repoId}                 — remover repo
 *   POST   /projects/{id}/git-repositories/{repoId}/index           — iniciar indexação (202)
 *   GET    /projects/{id}/git-repositories/{repoId}/index/status    — status da indexação
 *   GET    /projects/{id}/git-repositories/graph-status             — estado do grafo por repo
 *
 * OAuth Git:
 *   GET    /auth/git/connect?provider={github|bitbucket}&redirect_uri=...
 *   GET    /auth/git/callback?code=...&state=...                    — callback
 *   GET    /auth/git/providers                                      — providers conectados
 *
 * Clean / SOLID:
 *   - useIndexingStatus usa refetchInterval configurável (polling SSE-like).
 *   - useStartIndexing retorna a mutation que dispara POST .../index.
 *   - Sem lógica de UI.
 *
 * Para lote 2 (import/agents): este arquivo não precisa de alteração.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useApi } from './client';

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const gitKeys = {
  all:            ()                     => ['git'],
  repos:          (projectId)            => ['git', projectId, 'repos'],
  indexStatus:    (projectId, repoId)    => ['git', projectId, repoId, 'index-status'],
  graphStatus:    (projectId)            => ['git', projectId, 'graph-status'],
  providers:      ()                     => ['git', 'providers'],
  githubRepos:    (provider)             => ['git', 'repos', provider],
  githubBranches: (provider, repo)       => ['git', 'branches', provider, repo],
  githubOrgs:     (provider)             => ['git', 'orgs', provider],
};

// ─── Queries ──────────────────────────────────────────────────────────────────

/**
 * useGitProviders — providers OAuth Git conectados ao tenant.
 * Usado no Step 2 do Wizard para mostrar GitHub/Bitbucket.
 */
export function useGitProviders() {
  const api = useApi();
  return useQuery({
    queryKey: gitKeys.providers(),
    queryFn:  () => api.get('/auth/git/providers'),
    staleTime: 60_000,
  });
}

/**
 * useGitHubRepositories — repositórios reais do GitHub acessíveis pelo token
 * configurado (GITHUB_WRITE_TOKEN). Popula o seletor do wizard.
 *
 * Cada item: { full_name, default_branch, private, description }.
 * `enabled` permite só buscar quando o GitHub está conectado.
 *
 * @param {boolean} enabled
 */
export function useGitHubRepositories(enabled = true, provider = 'github') {
  const api = useApi();
  return useQuery({
    queryKey: gitKeys.githubRepos(provider),
    queryFn:  () => api.get(`/auth/git/repositories?provider=${provider}`),
    enabled:  Boolean(enabled),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * useCreateGitHubRepository — cria um repositório no provider configurado.
 * Body: { name, private, description, org?, provider }. Invalida a lista ao criar.
 * Requer dashboard_projects:create_edit.
 */
export function useCreateGitHubRepository() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/auth/git/repositories', body),
    onSuccess: (_data, body) => {
      queryClient.invalidateQueries({ queryKey: gitKeys.githubRepos(body?.provider ?? 'github') });
    },
  });
}

/**
 * useGitHubOrgs — orgs (GitHub) / workspace (Bitbucket) para escolher o owner.
 * @param {boolean} enabled
 * @param {string} provider
 */
export function useGitHubOrgs(enabled = true, provider = 'github') {
  const api = useApi();
  return useQuery({
    queryKey: gitKeys.githubOrgs(provider),
    queryFn:  () => api.get(`/auth/git/orgs?provider=${provider}`),
    enabled:  Boolean(enabled),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * useGitHubBranches — branches reais de um repositório GitHub via token.
 * @param {string|null} repoFullName — 'owner/nome'
 * @param {boolean} enabled
 */
export function useGitHubBranches(repoFullName, enabled = true, provider = 'github') {
  const api = useApi();
  return useQuery({
    queryKey: gitKeys.githubBranches(provider, repoFullName),
    queryFn:  () => api.get(`/auth/git/branches?provider=${provider}&repo=${encodeURIComponent(repoFullName)}`),
    enabled:  Boolean(enabled && repoFullName),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * useGitRepositories — lista repositórios de um projeto.
 * @param {string|null} projectId
 */
export function useGitRepositories(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: gitKeys.repos(projectId),
    queryFn:  () => api.get(`/projects/${projectId}/git-repositories`),
    enabled:  Boolean(projectId),
    staleTime: 30_000,
  });
}

/**
 * useIndexingStatus — status de indexação de um repositório.
 *
 * Faz polling via refetchInterval enquanto o status for 'indexing' ou 'pending'.
 * Para de fazer polling quando status for 'completed' ou 'failed'.
 *
 * Campos esperados do backend:
 *   { status: 'pending'|'indexing'|'completed'|'failed', progress: 0-100,
 *     indexed_files: number, total_files: number, error?: string }
 *
 * @param {string|null} projectId
 * @param {string|null} repoId
 * @param {boolean} enabled — controla se o polling está ativo
 */
export function useIndexingStatus(projectId, repoId, enabled = false) {
  const api = useApi();
  return useQuery({
    queryKey: gitKeys.indexStatus(projectId, repoId),
    queryFn:  () =>
      api.get(`/projects/${projectId}/git-repositories/${repoId}/index/status`),
    enabled:  Boolean(projectId && repoId && enabled),
    // Polling a cada 2s enquanto não concluído (backend: done|failed|cancelled)
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'done' || status === 'failed' || status === 'cancelled') return false;
      return 2_000;
    },
    // Manter dado anterior entre refetches para não piscar a UI
    placeholderData: (prev) => prev,
    staleTime: 0,
  });
}

/**
 * useGraphStatus — estado do GRAFO de código de cada repositório do projeto.
 *
 * Resposta: { project_id, exige_grafo, unificado, repos: { [repoId]: { ok, estado,
 *   motivo, branch, branch_cadastrada, branch_origem, commit, nos, arestas, arquivos,
 *   built_at } } }.
 *
 * Enquanto algum grafo está "construindo" repete a cada 5 s; fora isso, quem
 * invalida é o fim da indexação (ver IndexingProgressBar).
 *
 * @param {string|null} projectId
 */
export function useGraphStatus(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: gitKeys.graphStatus(projectId),
    queryFn:  () => api.get(`/projects/${projectId}/git-repositories/graph-status`),
    enabled:  Boolean(projectId),
    staleTime: 10_000,
    refetchInterval: (query) => {
      const repos = Object.values(query.state.data?.repos ?? {});
      return repos.some((g) => g?.estado === 'construindo') ? 5_000 : false;
    },
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

/**
 * useAddGitRepository — adiciona um repositório ao projeto.
 * @param {string} projectId
 */
export function useAddGitRepository(projectId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) =>
      api.post(`/projects/${projectId}/git-repositories`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gitKeys.repos(projectId) });
    },
  });
}

/**
 * useRemoveGitRepository — remove um repositório do projeto.
 * @param {string} projectId
 */
export function useRemoveGitRepository(projectId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (repoId) =>
      api.delete(`/projects/${projectId}/git-repositories/${repoId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gitKeys.repos(projectId) });
    },
  });
}

/**
 * useUpdateRepoRole — confirma/edita o papel de um repositório do projeto.
 * PATCH /projects/{id}/git-repositories/{repoId}/role com body { role }.
 * Papéis válidos: frontend | backend | database | infra | unknown.
 * Após sucesso, invalida a lista para refletir o novo papel.
 * @param {string} projectId
 */
export function useUpdateRepoRole(projectId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ repoId, role }) =>
      api.patch(`/projects/${projectId}/git-repositories/${repoId}/role`, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gitKeys.repos(projectId) });
    },
  });
}

/**
 * useStartIndexing — dispara a indexação de um repositório (POST → 202).
 * Quem chama deve passar `repoId` no mutationFn.
 *
 * Após sucesso, invalida o status para que o polling comece.
 * @param {string} projectId
 */
export function useStartIndexing(projectId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (repoId) =>
      api.post(`/projects/${projectId}/git-repositories/${repoId}/index`, {}),
    onSuccess: (_data, repoId) => {
      // Invalida o status para forçar re-query (inicia o polling)
      queryClient.invalidateQueries({
        queryKey: gitKeys.indexStatus(projectId, repoId),
      });
    },
  });
}

/**
 * usePublicIndex — 3ª opção: indexa um repo GitHub PÚBLICO por URL (sem conta
 * conectada). POST /projects/{id}/git-repositories/public-index → {url, branch}.
 * Retorna { repo_id, full_name, workflow_id, ... } — use repo_id p/ pollar status.
 */
export function usePublicIndex(projectId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ url, branch }) =>
      api.post(`/projects/${projectId}/git-repositories/public-index`, {
        url,
        branch: branch || 'main',
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: gitKeys.repos(projectId) });
      if (data?.repo_id) {
        queryClient.invalidateQueries({
          queryKey: gitKeys.indexStatus(projectId, data.repo_id),
        });
      }
    },
  });
}

// ─── Helpers OAuth ───────────────────────────────────────────────────────────

/**
 * buildGitOAuthUrl — constrói a URL de redirecionamento OAuth Git.
 *
 * O backend retorna a URL de autorização do provider. Como é um redirect externo,
 * não usamos mutation — só a URL.
 *
 * @param {string} provider — 'github' | 'bitbucket'
 * @param {string} baseApiUrl — VITE_API_URL
 * @returns {string} URL de redirecionamento
 */
export function buildGitOAuthUrl(provider, baseApiUrl) {
  const redirectUri = `${window.location.origin}/auth/git/callback`;
  return `${baseApiUrl}/auth/git/connect?provider=${provider}&redirect_uri=${encodeURIComponent(redirectUri)}`;
}

/**
 * useGitOAuthConnect — inicia o fluxo OAuth Git para um provider.
 * Abre window.location.href para o fluxo de autorização.
 */
export function useGitOAuthConnect() {
  const baseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';

  const connectProvider = useCallback((provider) => {
    const url = buildGitOAuthUrl(provider, baseUrl);
    window.location.href = url;
  }, [baseUrl]);

  return { connectProvider };
}
